/**
 * RiskCompositeDashboard — Unified Stock Risk Score Dashboard
 *
 * Aggregates ML risk, Wyckoff phase, trap signals, market regime & liquidity
 * into a single 0-100 risk score per stock with color-coded grades (A-D).
 *
 * NEW LAYOUT:
 * - Row 1: 4 Hero Metric Cards
 * - Row 2: Top Risk Banner (if HIGH/EXTREME exists)
 * - Row 3: Market Context + Sector Risk (2 columns)
 * - Row 4: Full-width Stocks Table
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, Paper, Chip, Alert, IconButton, CircularProgress, Snackbar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel,
  Tooltip, TextField, InputAdornment, Drawer,
  Button, ToggleButton, ToggleButtonGroup, LinearProgress, Skeleton,
} from '@mui/material';
import {
  ShieldOutlined as ShieldIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Search as SearchIcon,
  Calculate as CalculateIcon,
  Close as CloseIcon,
  Flag as FlagIcon,
  Assessment as AssessmentIcon,
  BubbleChart as BubbleIcon,
  TrendingUp as TrendUpIcon,
  TrendingDown as TrendDownIcon,
  Speed as SpeedIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  RemoveCircle as RemoveCircleIcon,
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Cell,
  Tooltip as RTooltip, ResponsiveContainer,
  ComposedChart, Area, Line,
} from 'recharts';
import { riskCompositeAPI } from '../services/api';

/* ═══════════════════════════════════════════════════════════════
   DESIGN TOKENS
   ═══════════════════════════════════════════════════════════════ */
const GRADE = {
  A: { label: 'LOW',     bg: '#22c55e', light: '#f0fdf4', text: '#15803d', emoji: '🟢' },
  B: { label: 'MEDIUM',  bg: '#eab308', light: '#fefce8', text: '#a16207', emoji: '🟡' },
  C: { label: 'HIGH',    bg: '#ef4444', light: '#fef2f2', text: '#b91c1c', emoji: '🟠' },
  D: { label: 'EXTREME', bg: '#a855f7', light: '#faf5ff', text: '#7e22ce', emoji: '🔴' },
};

const LABEL_CLR = {
  LOW:     { bg: '#22c55e', light: '#f0fdf4', text: '#15803d' },
  MEDIUM:  { bg: '#eab308', light: '#fefce8', text: '#a16207' },
  HIGH:    { bg: '#ef4444', light: '#fef2f2', text: '#b91c1c' },
  EXTREME: { bg: '#a855f7', light: '#faf5ff', text: '#7e22ce' },
};

const COMPONENT_COLORS = {
  ml_score:        '#ef4444',
  phase_score:     '#3b82f6',
  trap_score:      '#f59e0b',
  market_risk:     '#8b5cf6',
  liquidity_score: '#06b6d4',
};

const FLAG_SEVERITY_CLR = {
  critical: '#a855f7',
  high:     '#ef4444',
  warning:  '#eab308',
  info:     '#6b7280',
};


/* ── Score Gauge (mini) ─────────────────────────────────── */
const ScoreGauge = ({ score, size = 36, fontSize = 11 }) => {
  const grade = score >= 75 ? 'D' : score >= 50 ? 'C' : score >= 25 ? 'B' : 'A';
  const clr = GRADE[grade];
  return (
    <Box sx={{
      width: size, height: size, borderRadius: '50%',
      border: `3px solid ${clr.bg}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      bgcolor: `${clr.bg}0D`,
    }}>
      <Typography sx={{ fontSize, fontWeight: 800, color: clr.text }}>
        {Math.round(score)}
      </Typography>
    </Box>
  );
};


/* ── Component bar (stacked horizontal) ─────────────────── */
const ComponentBar = ({ ml, phase, trap, market, liquidity }) => {
  const items = [
    { key: 'ML', val: ml, color: COMPONENT_COLORS.ml_score, weight: 0.45 },
    { key: 'Phase', val: phase, color: COMPONENT_COLORS.phase_score, weight: 0.20 },
    { key: 'Trap', val: trap, color: COMPONENT_COLORS.trap_score, weight: 0.10 },
    { key: 'Market', val: market, color: COMPONENT_COLORS.market_risk, weight: 0.15 },
    { key: 'Liq', val: liquidity, color: COMPONENT_COLORS.liquidity_score, weight: 0.10 },
  ].filter(i => i.val != null);

  return (
    <Tooltip
      arrow
      title={
        <Box sx={{ p: 0.5 }}>
          {items.map(i => (
            <Typography key={i.key} sx={{ fontSize: 11 }}>
              {i.key} ({(i.weight * 100).toFixed(0)}%): <b>{(i.val * 100).toFixed(1)}</b>
            </Typography>
          ))}
        </Box>
      }
    >
      <Box sx={{ display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden', width: '100%', minWidth: 100, bgcolor: '#f3f4f6' }}>
        {items.map(i => (
          <Box
            key={i.key}
            sx={{
              width: `${i.weight * 100}%`,
              bgcolor: i.color,
              opacity: 0.3 + 0.7 * Math.min(1, (i.val || 0)),
            }}
          />
        ))}
      </Box>
    </Tooltip>
  );
};


/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════ */
const RiskCompositeDashboard = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [overview, setOverview] = useState(null);
  const [stocks, setStocks] = useState([]);
  const [marketContext, setMarketContext] = useState(null);
  const [search, setSearch] = useState('');
  const [filterLabel, setFilterLabel] = useState('ALL');
  const [sortField, setSortField] = useState('risk_score');
  const [sortDir, setSortDir] = useState('desc');
  const [calculating, setCalculating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Detail dialog
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailTicker, setDetailTicker] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailHistory, setDetailHistory] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  /* ─── Fetch ─────────────────────────────────────────── */
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ovR, stR, mktR] = await Promise.allSettled([
        riskCompositeAPI.getOverview(),
        riskCompositeAPI.getStocks(undefined, undefined, 'risk_desc', 500),
        fetch('/api/market/breadth').then(r => r.json()),
      ]);
      if (ovR.status === 'fulfilled') setOverview(ovR.value.data);
      if (stR.status === 'fulfilled') setStocks(stR.value.data || []);
      if (mktR.status === 'fulfilled' && mktR.value.success) setMarketContext(mktR.value);
      if (ovR.status === 'rejected') setError(ovR.reason?.response?.data?.detail || t('riskComposite.failedToLoad'));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ─── Recalculate ────────────────────────────────────── */
  const handleRecalculate = useCallback(async () => {
    setCalculating(true);
    try {
      const res = await riskCompositeAPI.calculate(undefined, true);
      const d = res.data;
      setSnackbar({
        open: true, severity: 'success',
        message: t('riskComposite.recalculate.success', {
          n: d.n_stocks, saved: d.n_saved, seconds: d.elapsed_seconds,
        }),
      });
      await fetchData();
    } catch (e) {
      setSnackbar({
        open: true, severity: 'error',
        message: t('riskComposite.recalculate.error', {
          detail: e.response?.data?.detail || e.message,
        }),
      });
    } finally {
      setCalculating(false);
    }
  }, [t, fetchData]);

  /* ─── Open detail dialog ─────────────────────────────── */
  const openDetail = useCallback(async (ticker) => {
    setDetailTicker(ticker);
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailData(null);
    setDetailHistory([]);
    try {
      const [detR, histR] = await Promise.allSettled([
        riskCompositeAPI.getStockDetail(ticker),
        riskCompositeAPI.getStockHistory(ticker, 60),
      ]);
      if (detR.status === 'fulfilled') setDetailData(detR.value.data);
      if (histR.status === 'fulfilled') setDetailHistory((histR.value.data || []).reverse());
    } catch (_) { /* noop */ }
    finally { setDetailLoading(false); }
  }, []);

  /* ─── Sort handler ───────────────────────────────────── */
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  /* ─── Computed ───────────────────────────────────────── */
  const filtered = useMemo(() => {
    let data = stocks;
    if (filterLabel !== 'ALL') {
      data = data.filter(s => s.risk_label === filterLabel);
    }
    if (search) {
      const q = search.toLowerCase();
      data = data.filter(s => s.ticker.toLowerCase().includes(q));
    }
    // Sort
    data = [...data].sort((a, b) => {
      const va = a[sortField] ?? 0;
      const vb = b[sortField] ?? 0;
      if (typeof va === 'string') return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      return sortDir === 'asc' ? va - vb : vb - va;
    });
    return data;
  }, [stocks, filterLabel, search, sortField, sortDir]);

  /* ─── Derived stats ──────────────────────────────────── */
  const dist = overview?.distribution || {};

  const nHighExtreme = (dist.HIGH || 0) + (dist.EXTREME || 0);

  // Sector risk aggregation
  const sectorRisks = useMemo(() => {
    if (!stocks || stocks.length === 0) return [];
    const sectorMap = {};
    stocks.forEach(s => {
      const sector = s.sector || 'Other';
      if (!sectorMap[sector]) {
        sectorMap[sector] = { sector, stocks: [], total: 0, avgRisk: 0, high: 0, extreme: 0 };
      }
      sectorMap[sector].stocks.push(s);
      sectorMap[sector].total++;
      sectorMap[sector].avgRisk += s.risk_score;
      if (s.risk_label === 'HIGH') sectorMap[sector].high++;
      if (s.risk_label === 'EXTREME') sectorMap[sector].extreme++;
    });
    Object.values(sectorMap).forEach(sec => {
      sec.avgRisk = sec.total > 0 ? sec.avgRisk / sec.total : 0;
    });
    return Object.values(sectorMap)
      .filter(sec => sec.sector !== 'Other')
      .sort((a, b) => b.avgRisk - a.avgRisk)
      .slice(0, 8);
  }, [stocks]);

  const nFlagged = stocks.filter(s => s.n_flags > 0).length;
  const hasPhaseData = stocks.some(s => s.phase_label != null);

  /* ═══════════════════════════════════════════════════════════
     ERROR
     ═══════════════════════════════════════════════════════════ */
  if (error) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 12, gap: 2 }}>
        <WarningIcon sx={{ fontSize: 48, color: '#ef4444' }} />
        <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
          {t('riskComposite.error')}
        </Typography>
        <Typography sx={{ fontSize: 13, color: '#9ca3af' }}>{error}</Typography>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchData} sx={{ mt: 2 }}>
          {t('riskComposite.retry')}
        </Button>
      </Box>
    );
  }

  /* ═══════════════════════════════════════════════════════════
     LOADING
     ═══════════════════════════════════════════════════════════ */
  if (loading) {
    return (
      <Box sx={{ maxWidth: 1600, mx: 'auto', pb: 4 }}>
        {/* Header skeleton */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, px: { xs: 1.5, md: 3 }, pt: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Skeleton variant="rounded" width={42} height={42} sx={{ borderRadius: 1.5 }} />
            <Box>
              <Skeleton width={220} height={28} />
              <Skeleton width={160} height={16} sx={{ mt: 0.5 }} />
            </Box>
          </Box>
          <Skeleton variant="rounded" width={140} height={32} />
        </Box>

        <Box sx={{ px: { xs: 1.5, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* Row 1: Overview skeleton */}
          {/* Row 1: Hero stat tiles skeleton */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
            {[1,2,3,4].map(i => (
              <Paper key={i} elevation={0} sx={{ p: 2, borderRadius: 1.5, border: '1px solid #e5e7eb' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Skeleton variant="circular" width={16} height={16} />
                  <Skeleton width={80} height={14} />
                </Box>
                <Skeleton width={60} height={28} />
              </Paper>
            ))}
          </Box>

          {/* Distribution bar skeleton */}
          <Skeleton variant="rounded" height={40} sx={{ borderRadius: 1.5 }} />

          {/* Banner skeleton */}
          <Skeleton variant="rounded" height={50} sx={{ borderRadius: 1.5 }} />

          {/* Row 3: Market Context + Sector Risk skeleton (side by side) */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '3fr 2fr' }, gap: 2 }}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 1.5, border: '1px solid #e5e7eb' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Skeleton width={200} height={20} />
              <Skeleton width={140} height={24} sx={{ borderRadius: 12 }} />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.5 }}>
              {[1,2,3,4].map(j => (
                <Box key={j} sx={{ p: 2, borderRadius: 1, bgcolor: '#f8fafc' }}>
                  <Skeleton width="60%" height={12} />
                  <Skeleton width="80%" height={22} sx={{ mt: 1 }} />
                  <Skeleton width="50%" height={10} sx={{ mt: 0.5 }} />
                </Box>
              ))}
            </Box>
          </Paper>

          <Paper elevation={0} sx={{ p: 2, borderRadius: 1.5, border: '1px solid #e5e7eb' }}>
            <Skeleton width={180} height={20} sx={{ mb: 2 }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
              {[1,2,3,4].map(j => (
                <Box key={j} sx={{ p: 1.5, borderRadius: 1, bgcolor: '#f8fafc' }}>
                  <Skeleton width="70%" height={14} sx={{ mb: 0.5 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Skeleton width={40} height={18} />
                    <Skeleton width={30} height={14} />
                  </Box>
                  <Skeleton width="100%" height={4} sx={{ borderRadius: 2 }} />
                </Box>
              ))}
            </Box>
          </Paper>
          </Box>

          {/* Row 4: Table skeleton */}
          <Paper elevation={0} sx={{ borderRadius: 1.5, border: '1px solid #e5e7eb' }}>
            <Box sx={{ px: 2.5, py: 1.5, bgcolor: '#f8fafc', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between' }}>
              <Skeleton width={180} height={20} />
              <Skeleton width={300} height={32} />
            </Box>
            <Box sx={{ p: 2 }}>
              {Array.from({ length: 15 }).map((_, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 2, py: 1, borderBottom: '1px solid #f3f4f6' }}>
                  <Skeleton width={80} />
                  <Skeleton width={60} />
                  <Skeleton width={50} />
                  <Skeleton width={100} />
                  <Skeleton sx={{ flex: 1 }} />
                  <Skeleton width={80} />
                  <Skeleton width={110} />
                  <Skeleton width={90} />
                </Box>
              ))}
            </Box>
          </Paper>
        </Box>
      </Box>
    );
  }

  /* ═══════════════════════════════════════════════════════════
     MAIN RENDER
     ═══════════════════════════════════════════════════════════ */
  return (
    <Box sx={{ maxWidth: 1600, mx: 'auto', pb: 4 }}>
      {/* ─── HEADER ─────────────────────────────────────── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, px: { xs: 1.5, md: 3 }, pt: 1, flexWrap: 'wrap', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 42, height: 42, borderRadius: 1.5,
            background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 14px rgba(59,130,246,0.3)',
          }}>
            <ShieldIcon sx={{ fontSize: 22, color: '#fff' }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: '#1e293b', letterSpacing: -0.3 }}>
              {t('riskComposite.pageTitle')}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 500 }}>
              {overview?.date && `${overview.date} · `}
              {overview?.n_stocks || 0} {t('riskComposite.stocks')}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="contained"
            startIcon={calculating ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <CalculateIcon />}
            onClick={!calculating ? handleRecalculate : undefined}
            disabled={calculating}
            sx={{
              height: 36, fontSize: 13, fontWeight: 600, textTransform: 'none',
              bgcolor: '#3b82f6', color: '#fff',
              borderRadius: 1,
              '&:hover': { bgcolor: '#2563eb' },
              '&:disabled': { bgcolor: '#94a3b8' },
            }}
          >
            {calculating ? t('riskComposite.actions.calculating') : t('riskComposite.actions.recalculate')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchData}
            sx={{
              height: 36, fontSize: 13, fontWeight: 600, textTransform: 'none',
              borderColor: '#e5e7eb', color: '#64748b',
              borderRadius: 1,
              '&:hover': { borderColor: '#3b82f6', color: '#3b82f6', bgcolor: '#eff6ff' },
            }}
          >
            {t('riskComposite.actions.refresh')}
          </Button>
        </Box>
      </Box>

      <Box sx={{ px: { xs: 1.5, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {/* ─── BANNERS ─────────────────────────────────── */}
        {calculating && (
          <Alert severity="info" icon={<CircularProgress size={16} />}
            sx={{ borderRadius: 1.5, '& .MuiAlert-message': { fontSize: 13, fontWeight: 600 } }}>
            {t('riskComposite.actions.calculatingBanner')}
          </Alert>
        )}

        {/* ═══════════════════════════════════════════════
            ROW 1: HERO STAT TILES (4 compact cards)
            ═══════════════════════════════════════════════ */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
          {/* Tile 1: Total Stocks */}
          <Paper elevation={0} sx={{ p: 2, borderRadius: 1.5, border: '1px solid #e5e7eb' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <AssessmentIcon sx={{ fontSize: 16, color: '#3b82f6' }} />
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b' }}>
                {t('riskComposite.overview.totalStocks')}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 28, fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>
              {overview?.n_stocks || 0}
            </Typography>
          </Paper>

          {/* Tile 2: Avg Risk Score */}
          <Paper elevation={0} sx={{ p: 2, borderRadius: 1.5, border: '1px solid #e5e7eb' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <SpeedIcon sx={{ fontSize: 16, color: '#8b5cf6' }} />
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b' }}>
                {t('riskComposite.overview.avgRiskScore')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
              <Typography sx={{
                fontSize: 28, fontWeight: 800, lineHeight: 1,
                color: (overview?.avg_risk_score || 0) >= 50 ? '#ef4444' : (overview?.avg_risk_score || 0) >= 25 ? '#eab308' : '#22c55e',
              }}>
                {(overview?.avg_risk_score || 0).toFixed(1)}
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#9ca3af', fontWeight: 600 }}>/100</Typography>
            </Box>
          </Paper>

          {/* Tile 3: Warnings */}
          <Paper elevation={0} sx={{
            p: 2, borderRadius: 1.5,
            border: nFlagged > 0 ? '1px solid #fde68a' : '1px solid #e5e7eb',
            bgcolor: nFlagged > 0 ? '#fffbeb' : '#fff',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <FlagIcon sx={{ fontSize: 16, color: nFlagged > 0 ? '#f59e0b' : '#22c55e' }} />
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b' }}>
                {t('riskComposite.overview.hasWarnings')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
              <Typography sx={{ fontSize: 28, fontWeight: 800, lineHeight: 1, color: nFlagged > 0 ? '#f59e0b' : '#22c55e' }}>
                {nFlagged}
              </Typography>
              <Typography sx={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>
                ({((nFlagged / (overview?.n_stocks || 1)) * 100).toFixed(0)}%)
              </Typography>
            </Box>
          </Paper>

          {/* Tile 4: High/Extreme Risk */}
          <Paper elevation={0} sx={{
            p: 2, borderRadius: 1.5,
            border: nHighExtreme > 0 ? '1px solid #fecaca' : '1px solid #e5e7eb',
            bgcolor: nHighExtreme > 0 ? '#fef2f2' : '#fff',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <ErrorIcon sx={{ fontSize: 16, color: nHighExtreme > 0 ? '#ef4444' : '#22c55e' }} />
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b' }}>
                {t('riskComposite.overview.highRiskAlert')}
              </Typography>
            </Box>
            {nHighExtreme > 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography sx={{ fontSize: 28, fontWeight: 800, lineHeight: 1, color: '#ef4444' }}>
                  {nHighExtreme}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {dist.HIGH > 0 && (
                    <Chip label={`${dist.HIGH} ${t('riskComposite.riskLevels.high')}`} size="small" sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: '#ef4444', color: '#fff' }} />
                  )}
                  {dist.EXTREME > 0 && (
                    <Chip label={`${dist.EXTREME} ${t('riskComposite.riskLevels.extreme')}`} size="small" sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: '#a855f7', color: '#fff' }} />
                  )}
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CheckCircleIcon sx={{ fontSize: 18, color: '#22c55e' }} />
                <Typography sx={{ fontSize: 12, color: '#15803d', fontWeight: 600 }}>
                  {t('riskComposite.overview.noHighRisk')}
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>

        {/* ── Risk Distribution Bar (compact inline) ──── */}
        <Paper elevation={0} sx={{ px: 2.5, py: 1.5, borderRadius: 1.5, border: '1px solid #e5e7eb' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', whiteSpace: 'nowrap' }}>
              {t('riskComposite.overview.riskDistribution')}
            </Typography>
            <Box sx={{ flex: 1, display: 'flex', height: 20, borderRadius: 2, overflow: 'hidden', minWidth: 200 }}>
              {['LOW', 'MEDIUM', 'HIGH', 'EXTREME'].map(k => {
                const cnt = dist[k] || 0;
                const pct = (cnt / (overview?.n_stocks || 1)) * 100;
                if (pct === 0) return null;
                return (
                  <Tooltip key={k} title={`${t(`riskComposite.riskLevels.${k.toLowerCase()}`)}: ${cnt} (${pct.toFixed(0)}%)`} arrow>
                    <Box sx={{
                      width: `${pct}%`, bgcolor: LABEL_CLR[k].bg, minWidth: pct > 0 ? 4 : 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'width 0.3s',
                    }}>
                      {pct >= 10 && (
                        <Typography sx={{ fontSize: 9, fontWeight: 700, color: '#fff' }}>{cnt}</Typography>
                      )}
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, flexShrink: 0 }}>
              {['LOW', 'MEDIUM', 'HIGH', 'EXTREME'].map(k => (
                <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: LABEL_CLR[k].bg }} />
                  <Typography sx={{ fontSize: 10, fontWeight: 600, color: '#64748b' }}>
                    {t(`riskComposite.riskLevels.${k.toLowerCase()}`)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Paper>

        {/* ═══════════════════════════════════════════════
            ROW 2: TOP RISK STOCKS (if any)
            ═══════════════════════════════════════════════ */}
        {nHighExtreme > 0 && (
          <Paper elevation={0} sx={{ px: 2.5, py: 1.5, borderRadius: 1.5, border: '2px solid #fecaca', bgcolor: '#fef2f2' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <WarningIcon sx={{ fontSize: 18, color: '#ef4444' }} />
              <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#991b1b' }}>
                {t('riskComposite.topRisk.title', { count: nHighExtreme })}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {stocks
                .filter(s => s.risk_label === 'HIGH' || s.risk_label === 'EXTREME')
                .slice(0, 15)
                .map(s => (
                  <Chip
                    key={s.ticker}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700 }}>{s.ticker}</Typography>
                        <Typography sx={{ fontSize: 9, opacity: 0.8 }}>({s.risk_score.toFixed(0)})</Typography>
                      </Box>
                    }
                    size="small"
                    onClick={() => openDetail(s.ticker)}
                    sx={{
                      height: 26, fontSize: 11, fontWeight: 700,
                      bgcolor: s.risk_label === 'EXTREME' ? '#a855f7' : '#ef4444',
                      color: '#fff',
                      cursor: 'pointer',
                      '&:hover': { opacity: 0.85 },
                    }}
                  />
                ))}
              {nHighExtreme > 15 && (
                <Chip 
                  label={t('riskComposite.topRisk.moreStocks', { count: nHighExtreme - 15 })} 
                  size="small" 
                  sx={{ height: 26, fontSize: 10, bgcolor: '#fff', color: '#64748b', border: '1px solid #e5e7eb', fontWeight: 600 }} 
                />
              )}
            </Box>
          </Paper>
        )}

        {/* ═══════════════════════════════════════════════
            ROW 3: MARKET CONTEXT + SECTOR RISK (side by side)
            ═══════════════════════════════════════════════ */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: sectorRisks.length > 0 ? '3fr 2fr' : '1fr' }, gap: 2 }}>
        <Paper elevation={0} sx={{ p: 2, borderRadius: 1.5, border: '1px solid #e5e7eb' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssessmentIcon sx={{ fontSize: 18, color: '#6366f1' }} />
              <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#1e293b' }}>
                {t('riskComposite.marketContext.title')}
              </Typography>
            </Box>
            {marketContext?.regime && (
              <Chip
                icon={
                  marketContext.regime === 'RISK_ON' ? (
                    <CheckCircleIcon sx={{ fontSize: 14, color: '#15803d' }} />
                  ) : marketContext.regime === 'NEUTRAL' ? (
                    <RemoveCircleIcon sx={{ fontSize: 14, color: '#a16207' }} />
                  ) : (
                    <CancelIcon sx={{ fontSize: 14, color: '#b91c1c' }} />
                  )
                }
                label={
                  marketContext.regime === 'RISK_ON' ? t('riskComposite.marketContext.regimeRiskOn') : 
                  marketContext.regime === 'NEUTRAL' ? t('riskComposite.marketContext.regimeNeutral') : 
                  t('riskComposite.marketContext.regimeRiskOff')
                }
                size="small"
                sx={{
                  height: 24, fontSize: 11, fontWeight: 700, px: 1.5,
                  bgcolor: marketContext.regime === 'RISK_ON' ? '#f0fdf4' : marketContext.regime === 'NEUTRAL' ? '#fefce8' : '#fef2f2',
                  color: marketContext.regime === 'RISK_ON' ? '#15803d' : marketContext.regime === 'NEUTRAL' ? '#a16207' : '#b91c1c',
                  border: `1px solid ${marketContext.regime === 'RISK_ON' ? '#bbf7d0' : marketContext.regime === 'NEUTRAL' ? '#fde68a' : '#fecaca'}`,
                  '& .MuiChip-icon': { ml: 0.5 },
                }}
              />
            )}
          </Box>

          {marketContext ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.5 }}>
              <Box sx={{ p: 2, borderRadius: 1, bgcolor: '#f8fafc', border: '1px solid #e5e7eb' }}>
                <Typography sx={{ fontSize: 10, color: '#64748b', mb: 1, fontWeight: 600 }}>{t('riskComposite.marketContext.vnindex')}</Typography>
                <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#1e293b', mb: 0.5 }}>
                  {marketContext.vnindex?.close.toFixed(2) || 'N/A'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {(marketContext.vnindex?.pct_change || 0) >= 0 ? (
                    <TrendUpIcon sx={{ fontSize: 14, color: '#22c55e' }} />
                  ) : (
                    <TrendDownIcon sx={{ fontSize: 14, color: '#ef4444' }} />
                  )}
                  <Typography sx={{
                    fontSize: 13, fontWeight: 700,
                    color: (marketContext.vnindex?.pct_change || 0) >= 0 ? '#22c55e' : '#ef4444',
                  }}>
                    {(marketContext.vnindex?.pct_change || 0) >= 0 ? '+' : ''}
                    {((marketContext.vnindex?.pct_change || 0) * 100).toFixed(2)}%
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ p: 2, borderRadius: 1, bgcolor: '#f8fafc', border: '1px solid #e5e7eb' }}>
                <Typography sx={{ fontSize: 10, color: '#64748b', mb: 1, fontWeight: 600 }}>{t('riskComposite.marketContext.marketRiskScore')}</Typography>
                <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#1e293b', mb: 0.5 }}>
                  {marketContext.market_risk_score?.toFixed(0) || 'N/A'}/100
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={marketContext.market_risk_score || 0}
                  sx={{
                    height: 6, borderRadius: 3, bgcolor: '#f1f5f9',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: (marketContext.market_risk_score || 0) >= 60 ? '#ef4444' : (marketContext.market_risk_score || 0) >= 40 ? '#eab308' : '#22c55e',
                      borderRadius: 3,
                    },
                  }}
                />
              </Box>
              
              <Box sx={{ p: 2, borderRadius: 1, bgcolor: '#f8fafc', border: '1px solid #e5e7eb' }}>
                <Typography sx={{ fontSize: 10, color: '#64748b', mb: 1, fontWeight: 600 }}>{t('riskComposite.marketContext.breadth')}</Typography>
                <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#1e293b', mb: 0.3 }}>
                  {marketContext.breadth?.adv_dec_ratio ? `${(marketContext.breadth.adv_dec_ratio * 100).toFixed(0)}%` : 'N/A'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {marketContext.breadth?.adv_dec_ratio >= 0.6 ? (
                    <CheckCircleIcon sx={{ fontSize: 14, color: '#22c55e' }} />
                  ) : marketContext.breadth?.adv_dec_ratio >= 0.4 ? (
                    <RemoveCircleIcon sx={{ fontSize: 14, color: '#eab308' }} />
                  ) : (
                    <CancelIcon sx={{ fontSize: 14, color: '#ef4444' }} />
                  )}
                  <Typography sx={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>
                    {marketContext.breadth?.adv_dec_ratio >= 0.6 ? t('riskComposite.marketContext.breadthPositive') : marketContext.breadth?.adv_dec_ratio >= 0.4 ? t('riskComposite.marketContext.breadthNeutral') : t('riskComposite.marketContext.breadthNegative')}
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ p: 2, borderRadius: 1, bgcolor: '#f8fafc', border: '1px solid #e5e7eb' }}>
                <Typography sx={{ fontSize: 10, color: '#64748b', mb: 1, fontWeight: 600 }}>{t('riskComposite.marketContext.recommendedExposure')}</Typography>
                <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#1e293b', mb: 0.5 }}>
                  {marketContext.target_exposure ? `${(marketContext.target_exposure * 100).toFixed(0)}%` : 'N/A'}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={(marketContext.target_exposure || 0) * 100}
                  sx={{
                    height: 6, borderRadius: 3, bgcolor: '#f1f5f9',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: (marketContext.target_exposure || 0) >= 0.7 ? '#22c55e' : (marketContext.target_exposure || 0) >= 0.4 ? '#eab308' : '#ef4444',
                      borderRadius: 3,
                    },
                  }}
                />
              </Box>
            </Box>
          ) : (
            <Typography sx={{ fontSize: 11, color: '#9ca3af', textAlign: 'center', py: 4 }}>
              {t('riskComposite.marketContext.loading')}
            </Typography>
          )}
        </Paper>

        {/* ── SECTOR RISK (right column) ── */}
        {sectorRisks.length > 0 && (
          <Paper elevation={0} sx={{ p: 2, borderRadius: 1.5, border: '1px solid #e5e7eb' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <BubbleIcon sx={{ fontSize: 18, color: '#8b5cf6' }} />
              <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#1e293b' }}>
                {t('riskComposite.sectorRisk.title', { count: sectorRisks.length })}
              </Typography>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1 }}>
              {sectorRisks.map(sec => {
                const riskLevel = sec.avgRisk >= 75 ? 'EXTREME' : sec.avgRisk >= 50 ? 'HIGH' : sec.avgRisk >= 25 ? 'MEDIUM' : 'LOW';
                const clr = LABEL_CLR[riskLevel];
                return (
                  <Box key={sec.sector} sx={{ 
                    p: 1.5, borderRadius: 1, 
                    bgcolor: clr.light,
                    border: `1px solid ${clr.bg}40`,
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.8 }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '65%' }}>
                        {sec.sector}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {(sec.high + sec.extreme) > 0 && (
                          <Chip
                            icon={<WarningIcon sx={{ fontSize: 10, color: '#dc2626' }} />}
                            label={sec.high + sec.extreme}
                            size="small"
                            sx={{
                              height: 18, fontSize: 8, fontWeight: 700,
                              bgcolor: '#fee2e2', color: '#dc2626',
                              '& .MuiChip-icon': { ml: 0.3 },
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                        <Typography sx={{ fontSize: 18, fontWeight: 800, color: clr.text }}>
                          {sec.avgRisk.toFixed(0)}
                        </Typography>
                        <Typography sx={{ fontSize: 10, color: '#9ca3af' }}>/100</Typography>
                      </Box>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>
                        {sec.total} {t('riskComposite.sectorRisk.totalStocks')?.toLowerCase() || 'stocks'}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, sec.avgRisk)}
                      sx={{
                        height: 4, borderRadius: 2, bgcolor: '#f1f5f9',
                        '& .MuiLinearProgress-bar': { bgcolor: clr.bg, borderRadius: 2 },
                      }}
                    />
                  </Box>
                );
              })}
            </Box>
          </Paper>
        )}
        </Box>{/* end Market+Sector row */}

        {/* ═══════════════════════════════════════════════
            ROW 4: STOCKS TABLE (Full Width)
            ═══════════════════════════════════════════════ */}
        <Paper elevation={0} sx={{ borderRadius: 1.5, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <Box sx={{ px: 2.5, py: 1.5, bgcolor: '#f8fafc', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShieldIcon sx={{ fontSize: 18, color: '#3b82f6' }} />
              <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#1e293b' }}>
                {t('riskComposite.stockTable.title')}
              </Typography>
              <Chip label={t('riskComposite.stockTable.count', { count: filtered.length })} size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: '#eff6ff', color: '#3b82f6' }} />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small" placeholder={t('riskComposite.stockTable.searchPlaceholder')}
                value={search}
                onChange={e => setSearch(e.target.value.toUpperCase())}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 14 }} /></InputAdornment> }}
                sx={{ width: 140, '& .MuiOutlinedInput-root': { borderRadius: 1.5, height: 32, fontSize: 11, bgcolor: '#fff' } }}
              />
              <ToggleButtonGroup
                size="small" value={filterLabel} exclusive
                onChange={(_, v) => v && setFilterLabel(v)}
                sx={{ height: 32 }}
              >
                <ToggleButton value="ALL" sx={{ fontSize: 10, px: 1.5, fontWeight: 600 }}>{t('riskComposite.stockTable.filters.all')}</ToggleButton>
                {['LOW', 'MEDIUM', 'HIGH', 'EXTREME'].map(k => (
                  <ToggleButton key={k} value={k} sx={{
                    fontSize: 10, px: 1.5, fontWeight: 600,
                    color: LABEL_CLR[k].text,
                    '&.Mui-selected': { bgcolor: `${LABEL_CLR[k].bg}20`, color: LABEL_CLR[k].text, fontWeight: 800 },
                  }}>
                    {k[0]}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          </Box>

          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow sx={{ 
                  bgcolor: '#f8fafc',
                  '& th': { 
                    fontSize: 11, fontWeight: 700, color: '#64748b', 
                    borderBottom: '2px solid #e5e7eb', py: 1.2, px: 1.5,
                    position: 'sticky', top: 0, bgcolor: '#f8fafc', zIndex: 1,
                    whiteSpace: 'nowrap',
                  } 
                }}>
                  <TableCell sx={{ width: 80 }}>
                    <TableSortLabel active={sortField === 'ticker'} direction={sortField === 'ticker' ? sortDir : 'asc'} onClick={() => handleSort('ticker')}>
                      {t('riskComposite.stockTable.headers.ticker')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sx={{ width: 100 }}>
                    <TableSortLabel active={sortField === 'risk_score'} direction={sortField === 'risk_score' ? sortDir : 'desc'} onClick={() => handleSort('risk_score')}>
                      {t('riskComposite.stockTable.headers.riskScore')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sx={{ width: 90 }}>
                    <TableSortLabel active={sortField === 'ml_prob'} direction={sortField === 'ml_prob' ? sortDir : 'desc'} onClick={() => handleSort('ml_prob')}>
                      ML Drop%
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ minWidth: 160 }}>
                    <Tooltip title={t('riskComposite.stockTable.headers.componentsTooltip')} arrow placement="top">
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        {t('riskComposite.stockTable.headers.components')}
                        <InfoIcon sx={{ fontSize: 11, color: '#94a3b8' }} />
                      </Box>
                    </Tooltip>
                  </TableCell>
                  {hasPhaseData && (
                    <TableCell align="center" sx={{ width: 90 }}>{t('riskComposite.stockTable.headers.phase')}</TableCell>
                  )}
                  <TableCell align="center" sx={{ width: 80 }}>
                    <Tooltip title="Market regime: ALLOW / WATCH / AVOID" arrow>
                      <span>Thị trường</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ width: 90 }}>
                    <TableSortLabel active={sortField === 'avg_value_20d'} direction={sortField === 'avg_value_20d' ? sortDir : 'desc'} onClick={() => handleSort('avg_value_20d')}>
                      Thanh khoản
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ minWidth: 200 }}>
                    <Tooltip title={t('riskComposite.stockTable.headers.warningsTooltip')} arrow placement="top">
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        <TableSortLabel active={sortField === 'n_flags'} direction={sortField === 'n_flags' ? sortDir : 'desc'} onClick={() => handleSort('n_flags')}>
                          {t('riskComposite.stockTable.headers.warnings')}
                        </TableSortLabel>
                        <InfoIcon sx={{ fontSize: 11, color: '#94a3b8' }} />
                      </Box>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={hasPhaseData ? 8 : 7} align="center" sx={{ py: 4, color: '#9ca3af' }}>
                      {t('riskComposite.stockTable.noData')}
                    </TableCell>
                  </TableRow>
                ) : filtered.map((s, idx) => {
                  const gc = GRADE[s.risk_grade] || GRADE.A;
                  const lc = LABEL_CLR[s.risk_label] || LABEL_CLR.LOW;
                  const mlPct = s.ml_prob != null ? (s.ml_prob * 100) : null;
                  const liqB = s.avg_value_20d != null ? (s.avg_value_20d / 1e9) : null;
                  return (
                    <TableRow
                      key={s.ticker}
                      hover
                      onClick={() => openDetail(s.ticker)}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: idx % 2 === 0 ? '#fff' : '#fafafa',
                        '&:hover': { bgcolor: `${gc.bg}08` },
                        transition: 'background 0.15s',
                      }}
                    >
                      {/* Ticker + Grade badge */}
                      <TableCell sx={{ py: 1.2, px: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          <Box sx={{ 
                            width: 24, height: 24, borderRadius: '6px',
                            bgcolor: gc.bg, color: '#fff',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 10, fontWeight: 800, flexShrink: 0,
                          }}>
                            {s.risk_grade}
                          </Box>
                          <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#1e293b' }}>
                            {s.ticker}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Risk Score + Level chip */}
                      <TableCell align="center" sx={{ py: 1.2, px: 1.5 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.3 }}>
                          <Typography sx={{ 
                            fontSize: 16, fontWeight: 800, lineHeight: 1,
                            color: lc.text,
                          }}>
                            {s.risk_score.toFixed(0)}
                          </Typography>
                          <Typography sx={{ fontSize: 9, fontWeight: 700, color: lc.text, opacity: 0.8 }}>
                            {s.risk_label === 'LOW' ? t('riskComposite.riskLevels.low') : 
                             s.risk_label === 'MEDIUM' ? t('riskComposite.riskLevels.medium') :
                             s.risk_label === 'HIGH' ? t('riskComposite.riskLevels.high') : t('riskComposite.riskLevels.extreme')}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* ML Drop % */}
                      <TableCell align="center" sx={{ py: 1.2, px: 1.5 }}>
                        {mlPct != null ? (
                          <Typography sx={{
                            fontSize: 12, fontWeight: 700,
                            color: mlPct >= 60 ? '#a855f7' : mlPct >= 40 ? '#ef4444' : mlPct >= 20 ? '#eab308' : '#22c55e',
                          }}>
                            {mlPct.toFixed(0)}%
                          </Typography>
                        ) : (
                          <Typography sx={{ fontSize: 11, color: '#cbd5e1' }}>—</Typography>
                        )}
                      </TableCell>

                      {/* Component Bar */}
                      <TableCell sx={{ py: 1.2, px: 1.5 }}>
                        <ComponentBar
                          ml={s.ml_score}
                          phase={s.phase_score}
                          trap={s.trap_score}
                          market={s.market_risk}
                          liquidity={s.liquidity_score}
                        />
                      </TableCell>

                      {/* Phase (hidden when no data) */}
                      {hasPhaseData && (
                        <TableCell align="center" sx={{ py: 1.2, px: 1.5 }}>
                          {s.phase_label ? (
                            <Chip label={s.phase_label} size="small" sx={{
                              height: 20, fontSize: 10, fontWeight: 600,
                              bgcolor: s.phase_label === 'distribution' || s.phase_label === 'markdown' ? '#fef2f2' :
                                       s.phase_label === 'markup' || s.phase_label === 'accumulation' ? '#f0fdf4' : '#f8fafc',
                              color: s.phase_label === 'distribution' || s.phase_label === 'markdown' ? '#dc2626' :
                                     s.phase_label === 'markup' || s.phase_label === 'accumulation' ? '#15803d' : '#64748b',
                              border: '1px solid',
                              borderColor: s.phase_label === 'distribution' || s.phase_label === 'markdown' ? '#fecaca' :
                                           s.phase_label === 'markup' || s.phase_label === 'accumulation' ? '#bbf7d0' : '#e5e7eb',
                            }} />
                          ) : (
                            <Typography sx={{ fontSize: 11, color: '#cbd5e1' }}>—</Typography>
                          )}
                        </TableCell>
                      )}

                      {/* Market Label */}
                      <TableCell align="center" sx={{ py: 1.2, px: 1.5 }}>
                        {s.market_label ? (
                          <Chip label={s.market_label} size="small" sx={{
                            height: 20, fontSize: 9, fontWeight: 700,
                            bgcolor: s.market_label === 'ALLOW' ? '#f0fdf4' : s.market_label === 'WATCH' ? '#fefce8' : '#fef2f2',
                            color: s.market_label === 'ALLOW' ? '#15803d' : s.market_label === 'WATCH' ? '#a16207' : '#dc2626',
                            border: '1px solid',
                            borderColor: s.market_label === 'ALLOW' ? '#bbf7d0' : s.market_label === 'WATCH' ? '#fde68a' : '#fecaca',
                          }} />
                        ) : (
                          <Typography sx={{ fontSize: 11, color: '#cbd5e1' }}>—</Typography>
                        )}
                      </TableCell>

                      {/* Liquidity (avg_value_20d) */}
                      <TableCell align="right" sx={{ py: 1.2, px: 1.5 }}>
                        {liqB != null ? (
                          <Typography sx={{
                            fontSize: 11, fontWeight: 600,
                            color: liqB >= 10 ? '#15803d' : liqB >= 1 ? '#1e293b' : '#ef4444',
                          }}>
                            {liqB >= 1 ? `${liqB.toFixed(1)}B` : `${(s.avg_value_20d / 1e6).toFixed(0)}M`}
                          </Typography>
                        ) : (
                          <Typography sx={{ fontSize: 11, color: '#cbd5e1' }}>—</Typography>
                        )}
                      </TableCell>

                      {/* Warnings + Top flag message */}
                      <TableCell sx={{ py: 1.2, px: 1.5 }}>
                        {s.n_flags > 0 ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                            <Box sx={{
                              display: 'flex', alignItems: 'center', gap: 0.3,
                              px: 0.8, py: 0.2, borderRadius: 1,
                              bgcolor: s.n_flags >= 3 ? '#fef2f2' : '#fffbeb',
                            }}>
                              <WarningIcon sx={{ fontSize: 12, color: s.n_flags >= 3 ? '#dc2626' : '#f59e0b' }} />
                              <Typography sx={{ fontSize: 11, fontWeight: 700, color: s.n_flags >= 3 ? '#dc2626' : '#f59e0b' }}>
                                {s.n_flags}
                              </Typography>
                            </Box>
                            {s.top_flag && (
                              <Tooltip title={s.top_flag} arrow placement="top">
                                <Typography sx={{
                                  fontSize: 10, color: '#64748b', fontWeight: 500,
                                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                  maxWidth: 260, cursor: 'help',
                                }}>
                                  {s.top_flag}
                                </Typography>
                              </Tooltip>
                            )}
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CheckCircleIcon sx={{ fontSize: 14, color: '#22c55e' }} />
                            <Typography sx={{ fontSize: 10, color: '#22c55e', fontWeight: 600 }}>OK</Typography>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {filtered.length > 0 && (
            <Box sx={{ px: 2.5, py: 1.5, bgcolor: '#f8fafc', borderTop: '1px solid #e5e7eb' }}>
              <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>
                Hiển thị {filtered.length} / {stocks.length} cổ phiếu
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>

      {/* ═══════════════════════════════════════════════════════
          DETAIL DIALOG
          ═══════════════════════════════════════════════════════ */}
      <Drawer
        anchor="right"
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        slotProps={{ paper: { sx: { width: { xs: '100%', sm: 440 }, p: 0, display: 'flex', flexDirection: 'column' } } }}
      >
        {/* Header */}
        <Box sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          borderBottom: '1px solid #e2e8f0', py: 1.5, px: 2.5, bgcolor: '#f8fafc',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShieldIcon sx={{ fontSize: 20, color: '#3b82f6' }} />
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#0f172a' }}>
                {detailTicker}
              </Typography>
            </Box>
            {detailData && (
              <>
                <ScoreGauge score={detailData.risk_score} size={32} fontSize={10} />
                <Chip label={detailData.risk_label} size="small" sx={{
                  height: 20, fontSize: 10, fontWeight: 700,
                  bgcolor: (LABEL_CLR[detailData.risk_label] || LABEL_CLR.LOW).bg,
                  color: '#fff',
                }} />
              </>
            )}
          </Box>
          <IconButton size="small" onClick={() => setDetailOpen(false)}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
        <Box sx={{ overflowY: 'auto', flex: 1, p: 0 }}>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={32} />
            </Box>
          ) : detailData ? (
            <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {/* ── Component Breakdown ────────── */}
              <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1e293b', mb: 1 }}>
                    {t('riskComposite.detailDialog.componentBreakdown')}
                  </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1px', bgcolor: '#e2e8f0', borderRadius: 2, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                  {[
                    { key: 'ML Risk', val: detailData.ml_score, weight: 45, color: COMPONENT_COLORS.ml_score, extra: detailData.ml_prob != null ? `prob=${(detailData.ml_prob * 100).toFixed(1)}%` : null },
                    { key: 'Phase', val: detailData.phase_score, weight: 20, color: COMPONENT_COLORS.phase_score, extra: detailData.phase_label },
                    { key: 'Trap', val: detailData.trap_score, weight: 10, color: COMPONENT_COLORS.trap_score },
                    { key: 'Market', val: detailData.market_risk, weight: 15, color: COMPONENT_COLORS.market_risk, extra: detailData.market_label },
                    { key: 'Liquidity', val: detailData.liquidity_score, weight: 10, color: COMPONENT_COLORS.liquidity_score, extra: detailData.avg_value_20d != null ? `${(detailData.avg_value_20d / 1e9).toFixed(1)}B` : null },
                  ].map(c => {
                    const pct = Math.min(100, (c.val || 0) * 100);
                    return (
                    <Box key={c.key} sx={{ bgcolor: '#fff', px: 1.5, py: 0.8, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      {/* Label + weight */}
                      <Box sx={{ width: 72, flexShrink: 0 }}>
                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#475569', lineHeight: 1.2 }}>{c.key}</Typography>
                        <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8', fontWeight: 600 }}>{c.weight}%</Typography>
                      </Box>
                      {/* Bar */}
                      <Box sx={{ flex: 1, height: 6, bgcolor: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                        <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: c.color, borderRadius: 3, transition: 'width 0.4s' }} />
                      </Box>
                      {/* Score */}
                      <Typography sx={{ fontSize: '0.88rem', fontWeight: 800, color: c.color, fontFamily: 'monospace', minWidth: 32, textAlign: 'right' }}>
                        {c.val != null ? pct.toFixed(0) : '—'}
                      </Typography>
                      {/* Extra */}
                      <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', minWidth: 52, textAlign: 'right', fontWeight: 500 }}>
                        {c.extra || ''}
                      </Typography>
                    </Box>
                    );
                  })}
                </Box>
              </Box>

              {/* ── SHAP Explanations ────────── */}
              {detailData.explain_top && detailData.explain_top.length > 0 && (
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1e293b', mb: 1.5 }}>
                    {t('riskComposite.detailDialog.shapTitle')}
                  </Typography>
                  <ResponsiveContainer width="100%" height={Math.max(120, detailData.explain_top.length * 32 + 30)}>
                    <BarChart
                      data={detailData.explain_top.map(e => ({
                        feature: e.feature.length > 20 ? e.feature.slice(0, 18) + '…' : e.feature,
                        shap: e.shap_value,
                        fullFeature: e.feature,
                        value: e.value,
                        direction: e.direction,
                      }))}
                      layout="vertical"
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                      <XAxis type="number" fontSize={10} tick={{ fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                      <YAxis type="category" dataKey="feature" fontSize={10} tick={{ fill: '#64748b' }} width={140} axisLine={false} tickLine={false} />
                      <RTooltip
                        content={({ active, payload }) => {
                          if (!active || !payload?.length) return null;
                          const d = payload[0].payload;
                          return (
                            <Paper elevation={0} sx={{ px: 1.5, py: 1, borderRadius: 1.5, border: '1px solid #e5e7eb' }}>
                              <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{d.fullFeature}</Typography>
                              <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                                Value: {d.value?.toFixed(4)} · SHAP: {d.shap?.toFixed(4)} ({d.direction})
                              </Typography>
                            </Paper>
                          );
                        }}
                      />
                      <Bar dataKey="shap" radius={[0, 4, 4, 0]} barSize={18}>
                        {detailData.explain_top.map((e, i) => (
                          <Cell key={i} fill={e.direction === 'risk_up' ? '#ef4444' : '#22c55e'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              )}

              {/* ── Risk Flags ───────────────── */}
              {detailData.risk_flags && detailData.risk_flags.length > 0 && (
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1e293b', mb: 1 }}>
                    {t('riskComposite.detailDialog.riskFlags', { count: detailData.risk_flags.length })}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                    {detailData.risk_flags.map((f, i) => (
                      <Paper key={i} elevation={0} sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5,
                        px: 2, py: 1, borderRadius: 1,
                        border: `1px solid ${FLAG_SEVERITY_CLR[f.severity] || '#e5e7eb'}33`,
                        bgcolor: `${FLAG_SEVERITY_CLR[f.severity] || '#f3f4f6'}08`,
                      }}>
                        <Chip label={f.severity.toUpperCase()} size="small" sx={{
                          height: 18, fontSize: 9, fontWeight: 800,
                          bgcolor: FLAG_SEVERITY_CLR[f.severity] || '#6b7280',
                          color: '#fff', minWidth: 55, textAlign: 'center',
                        }} />
                        <Chip label={f.source} size="small" variant="outlined" sx={{
                          height: 18, fontSize: 9, fontWeight: 600,
                          borderColor: '#d1d5db', color: '#6b7280',
                        }} />
                        <Typography sx={{ fontSize: 12, color: '#374151', flex: 1 }}>
                          {f.message}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                </Box>
              )}

              {/* ── History Chart ─────────────── */}
              {detailHistory.length > 1 && (
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1e293b', mb: 1.5 }}>
                    {t('riskComposite.detailDialog.historyChart')}
                  </Typography>
                  <ResponsiveContainer width="100%" height={220}>
                    <ComposedChart data={detailHistory}>
                      <defs>
                        <linearGradient id="griskArea" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.25} />
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="date" fontSize={10} tick={{ fill: '#9ca3af' }} axisLine={{ stroke: '#e5e7eb' }} tickLine={false}
                        tickFormatter={d => d?.slice(5)} />
                      <YAxis domain={[0, 100]} fontSize={10} tick={{ fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                      <RTooltip
                        content={({ active, payload, label }) => {
                          if (!active || !payload?.length) return null;
                          return (
                            <Paper elevation={0} sx={{ px: 1.5, py: 1, borderRadius: 1.5, border: '1px solid #e5e7eb', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                              <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.5 }}>{label}</Typography>
                              {payload.map((p, i) => (
                                <Typography key={i} sx={{ color: p.color, fontSize: 11 }}>
                                  {p.name}: {typeof p.value === 'number' ? p.value.toFixed(1) : p.value}
                                </Typography>
                              ))}
                            </Paper>
                          );
                        }}
                      />
                      <Area type="monotone" dataKey="risk_score" name="Risk Score" stroke="#3b82f6" fill="url(#griskArea)" strokeWidth={2} />
                      <Line type="monotone" dataKey="ml_score" name="ML" stroke={COMPONENT_COLORS.ml_score} strokeWidth={1.5} dot={false} connectNulls
                        strokeDasharray="4 2" />
                      <Line type="monotone" dataKey="phase_score" name="Phase" stroke={COMPONENT_COLORS.phase_score} strokeWidth={1.5} dot={false} connectNulls
                        strokeDasharray="4 2" />
                      <Line type="monotone" dataKey="market_risk" name="Market" stroke={COMPONENT_COLORS.market_risk} strokeWidth={1.5} dot={false} connectNulls
                        strokeDasharray="4 2" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ p: 4, textAlign: 'center', color: '#9ca3af' }}>
              {t('riskComposite.detailDialog.noData')}
            </Box>
          )}
        </Box>
      </Drawer>

      {/* ═══════════════════════════════════════════════════════
          SNACKBAR
          ═══════════════════════════════════════════════════════ */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))} sx={{ borderRadius: 1.5 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RiskCompositeDashboard;
